import React, { FC } from "react";
import { Form, Formik } from "formik";
import { ButtonPrimary, ButtonSecondary, CheckboxInput, Textarea } from "@shared/components";
import { Modal } from "@shared/components/Modal";
import * as Yup from "yup";

import { CancellationReasons } from "./types";

import "./index.scss";

export type ModalCancelNicheSubsProps = {
  onClose: () => void;
  onSuccess: (values: CancellationReasons) => void;
};

const cancellationReasons: Record<string, CancellationReasons["cancellation_reason"]> = {
  too_expensive: "Too expensive",
  data_not_working: "The data isn’t working",
  different_provider: "Found a different provider",
  not_enough_features: "Not enough features",
  switching_strategies: "Switching strategies",
  technical_issues: "Technical issues",
  lack_of_customer_support: "Lack of customer support",
  will_come_back: "Will come back",
  other: "Other",
};

const formValidationSchema = Yup.object().shape({
  cancellation_reason: Yup.string().required("Please select the reason to confirm cancellation"),
  cancellation_reason_other: Yup.string().when("cancellation_reason", {
    is: "other",
    then: Yup.string()
      .trim()
      .max(250, `Please keep your explanation under 250 characters`)
      .required(`When choosing 'Other', please explain why (up to 250 characters)`),
    otherwise: Yup.string().optional(),
  }),
});

export const ModalCancelNicheSubs: FC<ModalCancelNicheSubsProps> = (props) => {
  const { onClose, onSuccess } = props;

  const initialValues: CancellationReasons = { cancellation_reason: "", cancellation_reason_other: "" };

  const handleSubmit = async ({ cancellation_reason, cancellation_reason_other }: Record<string, string>) => {
    onSuccess({
      cancellation_reason: cancellationReasons[cancellation_reason],
      ...(cancellation_reason_other && { cancellation_reason_other }),
    });
  };

  return (
    <Modal>
      <Formik
        validationSchema={formValidationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setValues, setTouched, handleSubmit }) => {
          const showErrorMessage =
            touched.cancellation_reason && touched.cancellation_reason_other && Object.keys(errors).length;

          const errorMessage = showErrorMessage ? (
            <Modal.Error>{errors.cancellation_reason ?? errors.cancellation_reason_other}</Modal.Error>
          ) : null;

          return (
            <Form onSubmit={handleSubmit}>
              <Modal.Section>
                <Modal.Header>
                  <Modal.Title>Cancel Subscription</Modal.Title>
                  <Modal.Text>
                    Please confirm you want to cancel your subscription. We would appreciate it if you could share your
                    reason for the cancellation.
                  </Modal.Text>
                </Modal.Header>

                <Modal.Content>
                  <div className="modal-content-inner">
                    <div className="modal-content-title">Cancellation reason</div>

                    <CheckboxInput
                      name="too_expensive"
                      label={cancellationReasons.too_expensive}
                      value={values.cancellation_reason === "too_expensive" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "too_expensive", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="data_not_working"
                      label={cancellationReasons.data_not_working}
                      value={values.cancellation_reason === "data_not_working" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "data_not_working", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="different_provider"
                      label={cancellationReasons.different_provider}
                      value={values.cancellation_reason === "different_provider" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "different_provider", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="not_enough_features"
                      label={cancellationReasons.not_enough_features}
                      value={values.cancellation_reason === "not_enough_features" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "not_enough_features", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="switching_strategies"
                      label={cancellationReasons.switching_strategies}
                      value={values.cancellation_reason === "switching_strategies" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "switching_strategies", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="technical_issues"
                      label={cancellationReasons.technical_issues}
                      value={values.cancellation_reason === "technical_issues" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "technical_issues", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="lack_of_customer_support"
                      label={cancellationReasons.lack_of_customer_support}
                      value={values.cancellation_reason === "lack_of_customer_support" ? 1 : 0}
                      onChange={() => {
                        setValues(
                          { cancellation_reason: "lack_of_customer_support", cancellation_reason_other: "" },
                          true,
                        );
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="will_come_back"
                      label={cancellationReasons.will_come_back}
                      value={values.cancellation_reason === "will_come_back" ? 1 : 0}
                      onChange={() => {
                        setValues({ cancellation_reason: "will_come_back", cancellation_reason_other: "" }, true);
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    <CheckboxInput
                      name="other"
                      label={cancellationReasons.other}
                      value={values.cancellation_reason === "other" ? 1 : 0}
                      onChange={() => {
                        setValues({ ...values, cancellation_reason: "other" });
                        setTouched({ cancellation_reason: true });
                      }}
                    />

                    {values.cancellation_reason === "other" && (
                      <Textarea
                        name="cancellation_reason_other"
                        value={values.cancellation_reason_other}
                        placeholder="Enter your reason"
                        characterCounterLimit={250}
                        rows={5}
                        onChange={(evt: React.ChangeEvent<HTMLTextAreaElement>) => {
                          setValues({ ...values, cancellation_reason_other: evt.target.value });
                          setTouched({ cancellation_reason: true, cancellation_reason_other: true });
                        }}
                      />
                    )}
                  </div>
                </Modal.Content>

                {errorMessage}

                <Modal.Actions>
                  <ButtonSecondary onClick={onClose}>Back</ButtonSecondary>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>
                    Confirm Cancellation
                  </ButtonPrimary>
                </Modal.Actions>
              </Modal.Section>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
